<script setup>
// Sprint AGENDAR-MSG (15/07/2026): modal de agendamento de mensagem (Kinbox-style).
// Sprint AGENDAR-MSG.1: modo condicional texto/template pela janela de 24h da Meta.
//
// 2 modos de tela: create (agendar) e list (so os pendentes, aberto pelo chip).
//
// MODO DE ENTREGA (o coracao da .1): a Meta so aceita mensagem livre dentro de
// 24h da ultima incoming. Como o disparo e no FUTURO, o que importa nao e se a
// janela esta aberta AGORA e sim se vai estar aberta NA HORA AGENDADA. Por isso
// o modo e reavaliado a cada mudanca de horario:
//   - canal sem janela (Evolution QR)      -> sempre 'text'
//   - horario agendado < janela expira      -> 'text'
//   - horario agendado >= janela expira     -> 'template' (unico jeito de sair)
//
// FUSO (armadilha #47): <input type="datetime-local"> devolve horario LOCAL;
// toISOString() manda UTC; new Date(iso) renderiza local de volta. O Rails roda
// em UTC e nunca supoe fuso.
//
// Armadilha #22: zero codigo proprio de template — usa os helpers NATIVOS
// (templateHelper) e o getter NATIVO getFilteredWhatsAppTemplates (que ja filtra
// APPROVED, tira AUTHENTICATION/CSAT e componentes nao suportados).
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import {
  buildTemplateParameters,
  replaceTemplateVariables,
  findComponentByType,
  COMPONENT_TYPES,
  MEDIA_FORMATS,
} from 'dashboard/helper/templateHelper';

const props = defineProps({
  show: { type: Boolean, default: false },
  conversationId: { type: [Number, String], required: true },
  inboxId: { type: [Number, String], default: null },
  content: { type: String, default: '' },
  mode: { type: String, default: 'create' }, // 'create' | 'list'
  // canal cuja janela de 24h se aplica (WhatsApp). Evolution QR = false.
  isWindowChannel: { type: Boolean, default: false },
  canReply: { type: Boolean, default: true },
  windowExpiresAt: { type: String, default: null },
});
const emit = defineEmits(['close', 'scheduled']);

const store = useStore();

const draft = ref('');
const dtLocal = ref('');
const selectedTemplateName = ref('');
const processedParams = ref({});
const submitting = ref(false);
const cancellingId = ref(null);
const errorMsg = ref(null);

const pad = n => String(n).padStart(2, '0');

const toLocalInputValue = date =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;

const formatBR = iso => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const nextMonday = () => {
  const d = new Date();
  d.setHours(8, 0, 0, 0);
  const day = d.getDay();
  let add = (8 - day) % 7;
  if (add === 0) add = 7;
  d.setDate(d.getDate() + add);
  return d;
};

const shortcuts = computed(() => {
  const now = new Date();
  const t9 = new Date(now);
  t9.setDate(now.getDate() + 1);
  t9.setHours(9, 0, 0, 0);
  const t14 = new Date(now);
  t14.setDate(now.getDate() + 1);
  t14.setHours(14, 0, 0, 0);
  return [
    { label: 'Em 1 hora', date: new Date(now.getTime() + 3600 * 1000) },
    { label: 'Amanhã 9h', date: t9 },
    { label: 'Amanhã 14h', date: t14 },
    { label: 'Segunda 8h', date: nextMonday() },
  ];
});

const minLocal = computed(() => toLocalInputValue(new Date(Date.now() + 60 * 1000)));

const scheduledDate = computed(() => {
  if (!dtLocal.value) return null;
  const d = new Date(dtLocal.value);
  return Number.isNaN(d.getTime()) ? null : d;
});

const isValidTime = computed(
  () => !!scheduledDate.value && scheduledDate.value.getTime() > Date.now() + 50 * 1000
);

// ---- modo de entrega (texto livre x template) ----
const deliveryMode = computed(() => {
  if (!props.isWindowChannel) return 'text'; // Evolution QR: sem janela
  if (!props.windowExpiresAt) return 'template'; // Cloud sem incoming: nunca houve janela
  if (!scheduledDate.value) return props.canReply ? 'text' : 'template';
  return scheduledDate.value.getTime() < new Date(props.windowExpiresAt).getTime()
    ? 'text'
    : 'template';
});

// ---- templates (nativo) ----
const allTemplates = computed(() => {
  if (!props.inboxId) return [];
  return store.getters['inboxes/getFilteredWhatsAppTemplates'](props.inboxId) || [];
});

const hasMediaHeader = tpl => {
  const header = findComponentByType(tpl, COMPONENT_TYPES.HEADER);
  return !!header && MEDIA_FORMATS.includes(header.format);
};

// Templates com header de midia exigem upload de media_url — fora do escopo v1
// (mesma decisao da #47.37). Filtramos e avisamos, em vez de deixar o atendente
// escolher um template que falharia na hora do disparo.
const templates = computed(() => allTemplates.value.filter(t => !hasMediaHeader(t)));
const hiddenMediaCount = computed(() => allTemplates.value.length - templates.value.length);

const selectedTemplate = computed(
  () => templates.value.find(t => t.name === selectedTemplateName.value) || null
);

const bodyText = computed(() => {
  if (!selectedTemplate.value) return '';
  const body = findComponentByType(selectedTemplate.value, COMPONENT_TYPES.BODY);
  return body?.text || '';
});

const templateVarKeys = computed(() => Object.keys(processedParams.value.body || {}));

// ARMADILHA #19: `{{ }}` literal DENTRO de mustache quebra o @vue/compiler-core
// no build ("Unterminated string constant" — erro que aponta pro lugar errado).
// Construir a string aqui no script e a unica forma segura de exibir {{1}} na UI.
const vlabel = k => `{{${k}}}`;

const renderedTemplate = computed(() =>
  bodyText.value ? replaceTemplateVariables(bodyText.value, processedParams.value) : ''
);

const templateVarsFilled = computed(() => {
  const body = processedParams.value.body || {};
  return Object.values(body).every(v => String(v || '').trim().length > 0);
});

watch(selectedTemplate, tpl => {
  processedParams.value = tpl ? buildTemplateParameters(tpl, false) : {};
});

const categoryClass = cat =>
  cat === 'MARKETING'
    ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
    : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';

// ---- pendentes ----
const pending = computed(
  () => store.getters['bmtScheduledMessages/pendingByConv'][props.conversationId] || []
);

const canSubmit = computed(() => {
  if (props.mode !== 'create' || submitting.value || !isValidTime.value) return false;
  if (deliveryMode.value === 'template') {
    return !!selectedTemplate.value && templateVarsFilled.value;
  }
  return draft.value.trim().length > 0;
});

const applyShortcut = date => {
  dtLocal.value = toLocalInputValue(date);
  errorMsg.value = null;
};

const close = () => emit('close');

const submit = async () => {
  if (!canSubmit.value) return;
  errorMsg.value = null;
  submitting.value = true;
  try {
    const payload = {
      conversationId: props.conversationId,
      scheduledAt: scheduledDate.value.toISOString(), // sempre UTC
    };

    if (deliveryMode.value === 'template') {
      const tpl = selectedTemplate.value;
      // ARMADILHA #48: so mandamos as chaves que TEM valor. `namespace` nem
      // existe nos templates da Cloud API; mandar null derruba o
      // TEMPLATE_PARAMS_SCHEMA da Message ("must be of type string") na hora do
      // disparo — dias depois, quando ninguem esta olhando.
      const tp = { name: tpl.name, processed_params: processedParams.value };
      if (tpl.category) tp.category = tpl.category;
      if (tpl.language) tp.language = tpl.language;
      if (tpl.namespace) tp.namespace = tpl.namespace;

      payload.templateName = tpl.name;
      payload.templateParams = tp;
      // content = texto interpolado, pra bolha na conversa nao ficar vazia (#47.37)
      payload.content = renderedTemplate.value;
    } else {
      payload.content = draft.value.trim();
    }

    await store.dispatch('bmtScheduledMessages/create', payload);
    emit('scheduled', { mode: deliveryMode.value });
    close();
  } catch (error) {
    errorMsg.value =
      error?.response?.data?.error || 'Não foi possível agendar a mensagem.';
  } finally {
    submitting.value = false;
  }
};

const cancelOne = async id => {
  cancellingId.value = id;
  errorMsg.value = null;
  try {
    await store.dispatch('bmtScheduledMessages/cancel', {
      conversationId: props.conversationId,
      id,
    });
  } catch (error) {
    errorMsg.value =
      error?.response?.data?.error || 'Não foi possível cancelar o agendamento.';
  } finally {
    cancellingId.value = null;
  }
};

watch(
  () => props.show,
  isOpen => {
    if (!isOpen) return;
    errorMsg.value = null;
    dtLocal.value = '';
    selectedTemplateName.value = '';
    processedParams.value = {};
    // pre-preenche com o que ja estava no composer; se o editor estava
    // desabilitado (janela fechada) isso vem vazio e o atendente digita aqui
    draft.value = props.content || '';
    store.dispatch('bmtScheduledMessages/fetch', props.conversationId);
  }
);
</script>

<template>
  <Teleport to="body">
    <div
      v-if="show"
      class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4"
      @click.self="close"
    >
      <div
        class="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
      >
        <!-- Header -->
        <div
          class="flex items-center justify-between p-5 border-b border-slate-100 dark:border-slate-700"
        >
          <h2 class="text-lg font-semibold text-slate-900 dark:text-white">
            {{ mode === 'create' ? 'Agendar mensagem' : 'Mensagens agendadas' }}
          </h2>
          <button
            class="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
            @click="close"
          >
            <span class="i-lucide-x size-5" />
          </button>
        </div>

        <div class="p-5 flex flex-col gap-4">
          <template v-if="mode === 'create'">
            <!-- Quando enviar -->
            <div>
              <label
                class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1.5"
              >
                Quando enviar
              </label>
              <div class="flex flex-wrap gap-2 mb-3">
                <button
                  v-for="s in shortcuts"
                  :key="s.label"
                  class="px-3 py-1.5 rounded-full text-xs font-medium border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:border-emerald-500 hover:text-emerald-600 dark:hover:text-emerald-400"
                  @click="applyShortcut(s.date)"
                >
                  {{ s.label }}
                </button>
              </div>
              <!-- armadilha #43: datetime-local casa o $form-input-selector do
                   _base.scss (forca h-10); reset-base e o escape hatch oficial -->
              <input
                v-model="dtLocal"
                type="datetime-local"
                :min="minLocal"
                class="reset-base h-11 w-full border border-slate-200 dark:border-slate-600 rounded-lg px-3 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
              <p v-if="dtLocal && !isValidTime" class="text-xs text-red-600 mt-1">
                Escolha um horário pelo menos 1 minuto no futuro.
              </p>
              <p v-else-if="scheduledDate" class="text-xs text-slate-500 mt-1">
                Será enviada em {{ formatBR(scheduledDate.toISOString()) }}.
              </p>
            </div>

            <!-- Aviso: fora da janela -> só template -->
            <div
              v-if="deliveryMode === 'template'"
              class="flex gap-2 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3"
            >
              <span class="i-lucide-info size-4 text-amber-600 shrink-0 mt-0.5" />
              <p class="text-xs text-amber-800 dark:text-amber-300">
                No horário escolhido a
                <strong>janela de 24h da Meta</strong> estará fechada. Fora da
                janela só é possível enviar um <strong>template aprovado</strong>.
              </p>
            </div>

            <!-- MODO TEXTO -->
            <div v-if="deliveryMode === 'text'">
              <label
                class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1.5"
              >
                Mensagem
              </label>
              <textarea
                v-model="draft"
                rows="4"
                placeholder="Digite a mensagem que será enviada…"
                class="w-full border border-slate-200 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>

            <!-- MODO TEMPLATE -->
            <div v-else class="flex flex-col gap-3">
              <div>
                <label
                  class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1.5"
                >
                  Template aprovado
                </label>
                <div class="relative">
                  <!-- armadilha #15/#43: appearance-none do Tailwind NAO mata a
                       seta; vendor prefixes + background-image:none inline matam o
                       polygon do _base.scss:100 -->
                  <select
                    v-model="selectedTemplateName"
                    style="
                      -webkit-appearance: none;
                      -moz-appearance: none;
                      appearance: none;
                      background-image: none;
                    "
                    class="h-11 w-full border border-slate-200 dark:border-slate-600 rounded-lg pl-3 pr-9 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="">Selecione um template…</option>
                    <option v-for="t in templates" :key="t.name" :value="t.name">
                      {{ t.name }} ({{ t.language }})
                    </option>
                  </select>
                  <span
                    class="i-lucide-chevron-down size-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
                  />
                </div>
                <p v-if="!templates.length" class="text-xs text-red-600 mt-1">
                  Nenhum template aprovado disponível neste canal.
                </p>
                <p v-else-if="hiddenMediaCount" class="text-xs text-slate-400 mt-1">
                  {{ hiddenMediaCount }} template(s) com mídia no cabeçalho não
                  aparecem aqui.
                </p>
              </div>

              <!-- Categoria (informativa) -->
              <div v-if="selectedTemplate" class="flex items-center gap-2">
                <span
                  class="px-2 py-0.5 rounded text-[10px] font-semibold tracking-wide"
                  :class="categoryClass(selectedTemplate.category)"
                >
                  {{ selectedTemplate.category }}
                </span>
                <span class="text-xs text-slate-400">
                  {{ selectedTemplate.language }}
                </span>
              </div>

              <!-- Variáveis -->
              <div v-if="templateVarKeys.length" class="flex flex-col gap-2">
                <label
                  class="block text-sm font-medium text-slate-700 dark:text-slate-200"
                >
                  Variáveis
                </label>
                <div
                  v-for="k in templateVarKeys"
                  :key="k"
                  class="flex items-center gap-2"
                >
                  <span class="text-xs font-mono text-slate-500 w-10 shrink-0">
                    {{ vlabel(k) }}
                  </span>
                  <input
                    v-model="processedParams.body[k]"
                    type="text"
                    :placeholder="`Valor da variável ${k}`"
                    class="reset-base h-10 flex-1 border border-slate-200 dark:border-slate-600 rounded-lg px-3 text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
              </div>

              <!-- Preview -->
              <div v-if="selectedTemplate">
                <label
                  class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1.5"
                >
                  Prévia
                </label>
                <div
                  class="rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-900 p-3 text-sm text-slate-700 dark:text-slate-200 whitespace-pre-wrap max-h-32 overflow-y-auto"
                >
                  {{ renderedTemplate }}
                </div>
              </div>
            </div>
          </template>

          <!-- Pendentes -->
          <div v-if="pending.length">
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1.5"
            >
              Agendadas nesta conversa ({{ pending.length }})
            </label>
            <ul class="flex flex-col gap-2">
              <li
                v-for="p in pending"
                :key="p.id"
                class="flex items-start gap-2 rounded-lg border border-slate-200 dark:border-slate-600 p-2.5"
              >
                <span class="i-lucide-clock size-4 text-emerald-600 mt-0.5 shrink-0" />
                <div class="flex-1 min-w-0">
                  <p class="text-xs font-medium text-slate-900 dark:text-white">
                    {{ formatBR(p.scheduled_at) }}
                    <span class="font-normal text-slate-400">· {{ p.user_name }}</span>
                  </p>
                  <p class="text-xs text-slate-500 truncate">{{ p.content }}</p>
                  <p
                    v-if="p.has_template"
                    class="text-[10px] text-blue-600 dark:text-blue-400 font-medium mt-0.5"
                  >
                    template: {{ p.template_name }}
                  </p>
                </div>
                <button
                  :disabled="cancellingId === p.id"
                  class="text-xs font-medium text-red-600 hover:text-red-700 disabled:opacity-50 shrink-0"
                  @click="cancelOne(p.id)"
                >
                  {{ cancellingId === p.id ? '…' : 'Cancelar' }}
                </button>
              </li>
            </ul>
          </div>
          <p v-else-if="mode === 'list'" class="text-sm text-slate-500">
            Nenhuma mensagem agendada nesta conversa.
          </p>

          <p v-if="errorMsg" class="text-xs text-red-600">{{ errorMsg }}</p>
        </div>

        <!-- Footer -->
        <div
          class="flex justify-end gap-2 p-5 border-t border-slate-100 dark:border-slate-700"
        >
          <button
            class="px-4 py-2.5 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 font-medium"
            @click="close"
          >
            {{ mode === 'create' ? 'Cancelar' : 'Fechar' }}
          </button>
          <button
            v-if="mode === 'create'"
            :disabled="!canSubmit"
            class="px-6 py-2.5 rounded-lg bg-emerald-600 text-white font-medium hover:bg-emerald-700 disabled:bg-slate-300 dark:disabled:bg-slate-600 disabled:cursor-not-allowed"
            @click="submit"
          >
            {{ submitting ? 'Agendando…' : 'Agendar' }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>
